import { Check, Camera, Eye, FileText, Loader2, UploadCloud, X } from "lucide-react";
import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import { formatFileSize } from "../data";
import type { KycDocument } from "@/types/kyc";
import { kycService } from "@/services/apiServices";

interface UploadBlockProps {
  label: string;
  required?: boolean;
  helper?: string;
  value: KycDocument | null;
  onChange: (doc: KycDocument | null) => void;
  error?: string;
  camera?: boolean;
  retailerId?: string;
}

export default function UploadBlock({
  label,
  required,
  helper,
  value,
  onChange,
  error,
  camera,
  retailerId,
}: UploadBlockProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  function handleFile(file: File | undefined | null) {
    if (!file) return;
    if (!/\.(jpg|jpeg|png|pdf)$/i.test(file.name)) {
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("File size exceeds 5 MB limit.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      const formattedSize = formatFileSize(file.size);

      // Default client-side preview immediately
      const initialDoc: KycDocument = {
        docType: label,
        fileName: file.name,
        fileSize: formattedSize,
        uploadedAt: new Date().toISOString(),
        previewUrl: dataUrl,
      };

      if (retailerId) {
        setUploading(true);
        try {
          const res = await kycService.uploadDocument({
            retailerId,
            docType: label,
            fileName: file.name,
            fileData: dataUrl,
            fileSize: formattedSize,
          });

          if (res?.success && res.data?.document) {
            onChange(res.data.document);
          } else {
            onChange(initialDoc);
          }
        } catch {
          // Fallback to local data URL on network error
          onChange(initialDoc);
        } finally {
          setUploading(false);
        }
      } else {
        onChange(initialDoc);
      }
    };

    reader.readAsDataURL(file);
  }

  function handleInput(e: ChangeEvent<HTMLInputElement>) {
    handleFile(e.target.files?.[0]);
    e.target.value = "";
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  }

  return (
    <div className="rounded-[10px] border border-line p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-ink">
            {label}
            {required && <span className="ml-0.5 text-danger">*</span>}
          </p>
          {helper && <p className="mt-0.5 text-xs text-ink-muted">{helper}</p>}
        </div>
      </div>

      {uploading ? (
        <div className="mt-3 flex flex-col items-center justify-center rounded-lg border border-line bg-page p-6">
          <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
          <p className="mt-2 text-sm font-medium text-ink">Uploading {label}...</p>
          <p className="text-xs text-ink-muted">Processing file</p>
        </div>
      ) : value ? (
        <div className="mt-3 flex items-center gap-3 rounded-lg border border-line bg-page px-3.5 py-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-info">
            <FileText className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-ink">{value.fileName}</p>
            <p className="text-xs text-ink-muted">{value.fileSize}</p>
          </div>
          <Check className="h-5 w-5 shrink-0 text-success" />
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-secondary hover:bg-white hover:text-ink cursor-pointer"
            aria-label={`Preview ${value.fileName}`}
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-white hover:text-danger cursor-pointer"
            aria-label="Remove file"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`mt-3 flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed px-4 py-6 text-center transition-colors ${
            dragOver ? "border-primary-500 bg-primary-50" : "border-slate-300 hover:border-primary-400"
          }`}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-50 text-primary-500">
            {camera ? <Camera className="h-5 w-5" /> : <UploadCloud className="h-5 w-5" />}
          </span>
          <p className="mt-2 text-sm font-medium text-ink">Click to upload or drag and drop</p>
          <p className="mt-0.5 text-xs text-ink-muted">JPG, PNG or PDF, maximum 5 MB</p>
        </div>
      )}

      {error && <p className="mt-1.5 text-xs text-danger">{error}</p>}

      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf"
        className="hidden"
        onChange={handleInput}
      />

      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={value?.fileName ?? "Document preview"}
        size="lg"
        footer={
          <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
            <Button variant="secondary" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
          </div>
        }
      >
        {value && (
          <div className="flex flex-col items-center gap-4">
            {value.fileName.toLowerCase().endsWith(".pdf") ? (
              <div className="flex flex-col items-center justify-center gap-3 p-8">
                <FileText className="h-16 w-16 text-primary-500" />
                <p className="font-medium text-ink">{value.fileName}</p>
                <a
                  href={value.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600"
                >
                  Open PDF Document
                </a>
              </div>
            ) : (
              <img
                src={value.previewUrl}
                alt={value.fileName}
                className="max-h-[60vh] w-full rounded-lg border border-line object-contain"
              />
            )}
            <p className="text-[13px] text-ink-muted">
              {value.fileName} · {value.fileSize}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}